import { useSoftphone } from "@/contexts/SoftphoneContext";
import {
  IconDefinition,
  faClockRotateLeft,
  faGear,
  faGrip,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { FC, useState } from "react";
import { Collapse } from "react-bootstrap";
import { toast } from "react-toastify";
import "./Webphone.css";
import WebphoneHeader from "./WebphoneHeader";
import WebphoneCallQueue from "./WebphoneCallQueue";
import WebphoneInCall from "./WebphoneInCall";
import WebphoneKeypad from "./WebphoneKeypad";
import WebphoneMicError from "./WebphoneMicError";
import WebphoneRecents from "./WebphoneRecents";
import WebphoneSettings from "./WebphoneSettings";

type Tab = "recents" | "keypad" | "settings";

const TABS: { key: Tab; label: string; icon: IconDefinition }[] = [
  { key: "recents", label: "Récents", icon: faClockRotateLeft },
  { key: "keypad", label: "Clavier", icon: faGrip },
  { key: "settings", label: "Réglages", icon: faGear },
];

const WebphoneWidget: FC = () => {
  const {
    hasLine,
    registrationState,
    callState,
    incomingCall,
    calls,
    activeCall,
    busy,
    callError,
    hold,
    resume,
    micError,
    muted,
    extensionNumber,
    call,
    answer,
    hangup,
    mute,
    sendDtmf,
  } = useSoftphone();

  const [expanded, setExpanded] = useState(true);
  const [showDialer, setShowDialer] = useState(false);
  const [tab, setTab] = useState<Tab>("keypad");

  if (!hasLine) return null;

  const onCall = (number: string) => {
    call(number).catch(() => {
      toast.error("Impossible de passer l’appel. Vérifiez le message du téléphone.");
    });
  };

  const showingActiveCallScreen = calls.length > 0;
  const queuedCalls = calls.filter((call) => call.id !== activeCall?.id);
  const canDial = !activeCall && !incomingCall && !busy;
  // Errors are displayed persistently in the widget by the context.
  const handle = (operation: Promise<void>) => { void operation.catch(() => {}); };

  return (
    <div className="webphone-widget bg-white dark__bg-1100 rounded-3 shadow-lg">
      <WebphoneHeader
        extensionNumber={extensionNumber}
        registrationState={registrationState}
        callState={callState}
        hasIncomingCall={!!incomingCall}
        callCount={calls.length}
        hasActiveCall={!!activeCall}
        expanded={expanded}
        onToggle={() => setExpanded((old) => !old)}
      />
      <Collapse in={expanded}>
        <div>
          {micError && <WebphoneMicError kind={micError} />}

          {callError && <div role="alert" className="alert alert-danger mx-3 py-2 fs--1">{callError}</div>}
          {busy && <p role="status" className="px-3 fs--1 text-body-secondary">Mise à jour de l’appel…</p>}

          {activeCall && (
            <WebphoneInCall
              key={activeCall.id}
              number={activeCall.fromNumber}
              callState={activeCall.state === "active" ? "in-call" : "calling"}
              muted={muted}
              busy={busy}
              onHold={() => handle(hold(activeCall.id))}
              onHangup={() => handle(hangup(activeCall.id))}
              onToggleMute={() => mute(!muted)}
              onDtmf={sendDtmf}
            />
          )}
          <WebphoneCallQueue calls={queuedCalls} hasActiveCall={!!activeCall} busy={busy}
            onAnswer={(id) => handle(answer(id))}
            onResume={(id) => handle(resume(id))}
            onHangup={(id) => handle(hangup(id))} />

          {showingActiveCallScreen && canDial && (
            <div className="px-3 py-2">
              <button type="button" className="btn btn-sm btn-outline-primary" aria-expanded={showDialer} onClick={() => setShowDialer((value) => !value)}>
                {showDialer ? "Masquer le clavier" : "Nouvel appel"}
              </button>
            </div>
          )}
          {(!showingActiveCallScreen || (canDial && showDialer)) && (
            showingActiveCallScreen || tab === "keypad" ? <WebphoneKeypad onCall={onCall} />
              : tab === "settings" ? <WebphoneSettings /> : <WebphoneRecents />
          )}

          {!showingActiveCallScreen && (
            <div
              role="tablist"
              aria-label="Sections du webphone"
              className="d-flex justify-content-around border-top pt-2 pb-1"
            >
              {TABS.map(({ key, label, icon }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={classNames("webphone-tab border-0 bg-transparent px-2 pb-1", {
                    active: tab === key,
                  })}
                  onClick={() => setTab(key)}
                >
                  <span className="webphone-tab-icon-chip">
                    <FontAwesomeIcon icon={icon} />
                  </span>
                  <span className="webphone-tab-label fs--1 text-body-tertiary">{label}</span>
                </button>
              ))}
            </div>
          )}

          <p className="fs--2 text-body-tertiary text-center mb-0 pb-2">
            Propulsé par Kavkom
          </p>
        </div>
      </Collapse>
    </div>
  );
};

export default WebphoneWidget;
